<template>
    <validation-observer ref="form" v-slot="{ handleSubmit, setFieldValue, errors }" as="span">
        <modal :id="modalId" title="Send Message" scrollable @show="onShow" class="send-email-modal">
            <busy-modal :show="busy"></busy-modal>
            <b-form @submit.prevent="">
                <b-row v-if="isPayPalEmailMode">
                    <b-col>
                        <validation-provider name="sumPaid" rules="validFloat|required" >
                            <form-input
                                floating-label="Amount"
                                v-model="sumPaid"
                                :rightButton="{
                                    title: '',
                                    text: 'Amount to Confirm',
                                    variant: 'outline-primary',
                                    toggle: false,
                                }"
                                @handle-right-button-click="setAmountToConfirm()"
                                :rightButton2="{
                                    title: '',
                                    text: 'Amount to Balance',
                                    variant: 'outline-primary',
                                    toggle: false,
                                }"
                                @handle-right-button-2-click="setAmountToBalance()"
                                :degrade-on-small="true"
                                drop-down-text="Select Amount"
                                :error="errors.sumPaid"
                                @input="(val) => setFieldValue('sumPaid', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="to" rules="required|email" >
                            <form-input
                                ref="toInput"
                                v-if="!isAirbnbMessageAPI && !isVRBOMessageAPI && !isPayPalEmailMode"
                                floatingLabel="To"
                                v-model="to"
                                :rightButton="{ title: 'CC', text: 'CC', variant: 'outline-primary', toggle: true }"
                                @handle-right-button-click="toggleCC"
                                :rightButton2="{ title: 'BCC', text: 'BCC', variant: 'outline-primary', toggle: true }"
                                @handle-right-button-2-click="toggleBCC"
                                :error="errors.to"
                                @input="(val) => setFieldValue('to', val)"
                            />
                            <form-input
                                ref="toInput"
                                v-if="isAirbnbMessageAPI || isVRBOMessageAPI || isPayPalEmailMode"
                                floatingLabel="To"
                                v-model="to"
                                :error="errors.to"
                                @input="(val) => setFieldValue('to', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="isCC && !isAirbnbMessageAPI && !isVRBOMessageAPI && !isPayPalEmailMode" class="no-gutters">
                    <b-col md="3">
                        <form-select
                            floatingLabel="CC Mode"
                            :options="ccModeOptions"
                            v-model="ccMode"
                            @change="onCCModeChange"
                        />
                    </b-col>
                    <b-col md="9">
                        <validation-provider name="cc" rules="email" >
                            <form-input
                                floatingLabel="CC"
                                v-model="cc"
                                v-if="ccMode == 'OTHER'"
                                class="cc-email-input"
                                :error="errors.cc"
                                @input="(val) => setFieldValue('cc', val)"
                            />
                        </validation-provider>
                        <form-select
                            floatingLabel="CC"
                            :options="teamMemberOptions"
                            v-model="selectedCC"
                            v-if="ccMode == 'TEAM_MEMBER'"
                        />
                        <form-select
                            floatingLabel="CC"
                            :options="ownerOptions"
                            v-model="selectedCC"
                            v-if="ccMode == 'OWNER'"
                        />
                        <form-select
                            floatingLabel="CC"
                            :options="guestListOptions"
                            v-model="selectedCC"
                            v-if="ccMode == 'GUEST_LIST'"
                        />
                    </b-col>
                </b-row>
                <b-row v-if="isBCC && !isAirbnbMessageAPI && !isVRBOMessageAPI && !isPayPalEmailMode">
                    <b-col>
                        <validation-provider name="bcc" rules="email" >
                            <form-input
                                floatingLabel="BCC"
                                v-model="bcc"
                                :error="errors.bcc"
                                @input="(val) => setFieldValue('bcc', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="subject" rules="required">
                            <form-input
                                floatingLabel="Subject"
                                v-model="subject"
                                :error="errors.subject"
                                @input="(val) => setFieldValue('subject', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="!isAirbnbMessageAPI && !isVRBOMessageAPI">
                    <b-col>
                        <form-select
                            floatingLabel="PDF"
                            :options="pdfOptions"
                            v-model="selectedPDFOption"
                            :gray-first="true"
                        />
                    </b-col>
                </b-row>
                <b-row v-if="!isAirbnbMessageAPI && !isVRBOMessageAPI && !isPayPalEmailMode">
                    <b-col>
                        <form-select
                            floatingLabel="Document"
                            :options="documentOptions"
                            v-model="selectedDocumentOption"
                            :gray-first="true"
                        />
                    </b-col>
                </b-row>
                <b-row class="no-gutters" v-if="!isAirbnbMessageAPI && !isVRBOMessageAPI && !isPayPalEmailMode">
                    <b-col md="9">
                        <form-file
                            class="overflow-hidden pb-2"
                            floating-label="Attachment"
                            placeholder="Upload and attach a remote file ..."
                            v-model="attachment"
                        ></form-file>
                    </b-col>
                    <b-col md="3" class="pb-3 pl-md-2 pb-md-0">
                        <b-button
                            class="right-button"
                            variant="warning"
                            @click="clearFile"
                            :disabled="isNil(attachment)"
                            >Clear</b-button
                        >
                    </b-col>
                </b-row>
                <b-row class="no-gutters">
                    <b-col md="9">
                        <form-select
                            :options="emailResponseOptions"
                            v-model="selectedEmailResponseOption"
                            floatingLabel="Email Response"
                            placeholder="Insert email template response"
                        />
                    </b-col>
                    <b-col md="3" class="pb-3 pl-md-2 pb-md-0">
                        <b-button
                            class="right-button"
                            variant="primary"
                            @click="applyResponse"
                            :disabled="isNil(selectedEmailResponseOption)"
                            >Apply</b-button
                        >
                    </b-col>
                </b-row>

                <validation-provider name="message" rules="required">
                    <lx-editor
                        v-if="!isAirbnbMessageAPI && !isVRBOMessageAPI"
                        v-model="message"
                        :error="errors.message"
                        @input="(val) => setFieldValue('message', val)"
                    ></lx-editor>

                    <form-textarea
                        v-if="isAirbnbMessageAPI && !isVRBOMessageAPI"
                        v-model="message"
                        :error="errors.message"
                        rows="6"
                        @input="(val) => setFieldValue('message', val)"
                    ></form-textarea>
                </validation-provider>
            </b-form>

            <template #modal-footer="{ cancel }">
                <div class="w-100">
                    <b-button variant="outline-primary" class="float-left" :disabled="busy" @click="clearMessage()"> Clear </b-button>
                    <b-button variant="primary" class="float-right ml-2" :disabled="busy" @click="handleSubmit(_, sendEmail)">
                        Send
                    </b-button>
                    <b-button variant="secondary" class="float-right" :disabled="busy" @click="cancel()"> Cancel </b-button>
                </div>
            </template>
        </modal>
    </validation-observer>
</template>

<script>
import * as DOMPurify from 'dompurify';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import API from '../../api';
import { mapGetters } from 'vuex';
import {
    APP_EMAIL_RESPONSE_EMAIL,
    APP_EMAIL_RESPONSE_PAYPAL,
    APP_SEND_SIGNATURE_REQUEST,
    APP_EMAIL_MOCKUP_RESPONSES,
    RESERVATION_INVOICE_REPORT_TEMPLATE_EMAIL_ENDPOINT,
    RESERVATION_INVOICE_REPORT_TEMPLATE_PAYPAL_ENDPOINT,
    RESERVATION_INVOICE_UPLOADED_FILE_EMAIL_ENDPOINT,
    RESERVATION_INVOICE_CURRENT_TEAM_MEMBERS_ENDPOINT,
    RESERVATION_INVOICE_CURRENT_OWNERS_ENDPOINT,
    RESERVATION_INVOICE_SEND_EMAIL_ENDPOINT,
    RESERVATION_INVOICE_SEND_PAYPAL_INVOICE_ENDPOINT,
} from '../../endpoints';
import FormInput from '../generic/form-input/form-input.vue';
import FormFile from '../generic/form-file/form-file.vue';
import FormSelect from '../generic/form-select/form-select.vue';
import FormTextarea from '../generic/form-textarea/form-textarea.vue';
import LxEditor from '../generic/lx-editor/lx-editor.vue';
import Modal from '../generic/modal/modal.vue';
import BusyModal from '../generic/busy-modal/busy-modal.vue';
import { getCurrentInstance } from 'vue';

export default {
    name: 'SendEmailModal',
    components: { Modal, FormInput, FormSelect, FormTextarea, FormFile, LxEditor, BusyModal },
    props: {
        initialValues: {
            type: Object,
            default: () => ({}),
        },
        modalId: String,
        onUpdate: {
            type: Function,
            default: () => {},
        },
        onToggleCC: {
            type: Function,
            default: () => {},
        },
        onToggleBCC: {
            type: Function,
            default: () => {},
        },
        selectedReportTemplate: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            editorModules: {
                modules: {
                    toolbar: {
                        container: [
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [{ header: 1 }, { header: 2 }],
                            ['clean'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                        ],
                    },
                    htmlEditButton: {},
                },
            },
            pdfOptions: [],
            emailResponseOptions: [],
            documentOptions: [],
            teamMemberOptions: [],
            ownerOptions: [],
            guestListOptions: [],
            message: null,
            reservation: {},
            subject: '',
            selectedPDFOption: null,
            selectedEmailResponseOption: null,
            selectedDocumentOption: null,
            selectedCC: null,
            attachment: null,
            to: null,
            cc: null,
            bcc: null,
            isBCC: false,
            isCC: false,
            sumPaid: null,
            ccMode: 'OTHER',
            invalid: false,
            isAirbnbMessageAPI: false,
            isVRBOMessageAPI: false,
            busy: false,
            visible: true,
        };
    },
    created() {
        this.reservation = this.initialValues;
        this.isAirbnbMessageAPI = this.reservation.airbnb_messaging_api;
        this.isVRBOMessageAPI = this.reservation.vrbo_messaging_api;
    },
    computed: {
        ...mapGetters('header', ['userID', 'userCompany', 'userEmailSignature']),
        ccModeOptions() {
            let options = [{ value: 'OTHER', text: 'Other' }];
            if (this.teamMemberOptions.length > 0) {
                options.push({ value: 'TEAM_MEMBER', text: 'Team Member' });
            }
            if (this.ownerOptions.length > 0) {
                options.push({ value: 'OWNER', text: 'Owner' });
            }
            if (this.guestListOptions.length > 0) {
                options.push({ value: 'GUEST_LIST', text: 'Guest List' });
            }
            return options;
        },
        isPayPalEmailMode() {
            const instance = getCurrentInstance();
            return instance?.parent?.data?.emailMode === 'PAYPAL';
        },
    },
    methods: {
        isNil,
        async applyResponse() {
            if (!isNil(this.selectedEmailResponseOption)) {
                const option = this.emailResponseOptions.find(
                    (option) => option.value === this.selectedEmailResponseOption
                );
                if (!!option) {
                    this.subject = option.text;
                }

                try {
                    const res = await API.get(
                        APP_EMAIL_MOCKUP_RESPONSES(this.selectedEmailResponseOption, this.reservation.id)
                    );
                    this.message = this.cleanMessage(res.data);
                    this.$refs.form.setFieldValue('message', this.message);
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data' });
                }
            }
        },
        cleanMessage(text) {
            const config = {
                ADD_TAGS: ['center'],
                ADD_ATTR: [
                    'href',
                    'name',
                    'target',
                    'src',
                    'srcset',
                    'alt',
                    'title',
                    'width',
                    'height',
                    'loading',
                    'style',
                    'colspan',
                    'align',
                ],
            };
            let sanitizedText = DOMPurify.sanitize(text || '', config);
            return sanitizedText;
        },
        async onShow() {
            this.template = this.initialValues.template;

            this.$nextTick(() => {
                this.selectedPDFOption = null;
                this.selectedEmailResponseOption = null;
                this.selectedDocumentOption = null;
                this.selectedCC = null;
                this.attachment = null;
                this.isBCC = false;
                this.isCC = false;
                this.subject = `Rental from ${this.userCompany}`;
                if (!isNil(this.selectedReportTemplate) && !isNil(this.selectedReportTemplate.id)) {
                    this.subject = `${this.selectedReportTemplate.report_name} from ${this.userCompany}`;
                }
                this.onCCModeChange();
                if (!this.isPayPalEmailMode) {
                    this.$refs.toInput.clearButtonsStates();
                }
            });

            if (
                isEmpty(this.message) ||
                isNil(this.message) ||
                (!this.isPayPalEmailMode && this.message.includes('[PAYPAL_LINK]'))
            ) {
                this.clearMessage();
            }
            await this.fetchData();
            this.$refs.form.setValues({
                to: this.reservation.user.email,
                cc: this.cc,
                bcc: this.bcc,
                subject: this.subject,
                message: this.message,
                sumPaid: this.sumPaid,
            });
        },
        async fetchData() {
            try {
                let DOCUMENT_REPORT_TEMPLATE_API_ENDPOINT = RESERVATION_INVOICE_REPORT_TEMPLATE_EMAIL_ENDPOINT;
                if (this.isPayPalEmailMode) {
                    DOCUMENT_REPORT_TEMPLATE_API_ENDPOINT = RESERVATION_INVOICE_REPORT_TEMPLATE_PAYPAL_ENDPOINT;
                }
                const response = await API.get(DOCUMENT_REPORT_TEMPLATE_API_ENDPOINT());
                this.pdfOptions = [
                    {
                        value: null,
                        text: 'Attach a PDF template',
                    },
                ].concat(
                    response.data.map((item) => ({
                        value: item.id,
                        text: item.report_name,
                    }))
                );

                this.selectedPDFOption = this.selectedReportTemplate ? this.selectedReportTemplate.id : '';

                const documentResponse = await API.get(
                    RESERVATION_INVOICE_UPLOADED_FILE_EMAIL_ENDPOINT(this.reservation.id)
                );
                this.documentOptions = [
                    {
                        value: null,
                        text: 'Attach a previously uploaded file',
                    },
                ].concat(
                    documentResponse.data.map((item) => ({
                        value: item.id,
                        text: item.filename,
                    }))
                );

                this.selectedDocumentOption = null;

                const teamMembersResponse = await API.get(
                    RESERVATION_INVOICE_CURRENT_TEAM_MEMBERS_ENDPOINT(this.userID)
                );

                this.teamMemberOptions = teamMembersResponse.data.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));

                try {
                    const ownersResponse = await API.get(RESERVATION_INVOICE_CURRENT_OWNERS_ENDPOINT());

                    this.ownerOptions = ownersResponse.data.map((item) => ({
                        value: item.id,
                        text: item.name,
                    }));
                } catch (e) {
                    this.ownerOptions = [];
                }

                this.guestListOptions = this.reservation.guest_list.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));

                let EMAIL_RESPONSE_API_ENDPOINT = APP_EMAIL_RESPONSE_EMAIL;
                if (this.isPayPalEmailMode) {
                    EMAIL_RESPONSE_API_ENDPOINT = APP_EMAIL_RESPONSE_PAYPAL;
                }
                const emailResponse = await API.get(EMAIL_RESPONSE_API_ENDPOINT());
                this.emailResponseOptions = [
                    {
                        value: null,
                        text: 'Please select an email response',
                    },
                ].concat(
                    emailResponse.data.map((item) => ({
                        value: item.id,
                        text: item.title,
                    }))
                );

                this.selectedEmailResponseOption = null;

                if (this.isPayPalEmailMode) {
                    const defaultItem = emailResponse.data.find((item) => item.is_default_paypal_invoice);
                    this.selectedEmailResponseOption = defaultItem ? defaultItem.id : '';
                    await this.applyResponse();
                }

                this.to = this.reservation.user.email;
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch data' });
            }
        },
        async sendEmail() {
            try {
                this.busy = true;
                const data = new FormData();
                data.append('to', this.to);
                data.append('cc', this.cc);
                data.append('cc_id', this.selectedCC);
                data.append('bcc', this.bcc);
                data.append('email_body', this.cleanMessage(this.message));
                data.append('subject', this.subject);
                data.append('template_id', this.selectedPDFOption);
                data.append('document_id', this.selectedDocumentOption);
                data.append('cc_mode', this.ccMode);
                data.append('attachment', this.attachment);
                data.append('sum_paid', this.sumPaid);

                let API_ENDPOINT = RESERVATION_INVOICE_SEND_EMAIL_ENDPOINT;
                if (this.isPayPalEmailMode) {
                    API_ENDPOINT = RESERVATION_INVOICE_SEND_PAYPAL_INVOICE_ENDPOINT;
                }

                await API.patch(API_ENDPOINT(this.reservation.id), data);

                this.onUpdate();
                this.clearMessage();
                this.successToast({ text: 'Message successfully sent' });
                this.$bvModal.hide(this.modalId);
                this.visible = false;
            } catch (e) {
                this.errorToast({
                    text: e.response?.data?.errors || e.response?.data?.detail || 'Error: Could not send message.',
                });
            } finally {
                this.busy = false;
            }
            this.busy = false;
        },
        toggleCC(status) {
            this.isCC = status;
        },
        toggleBCC(status) {
            this.isBCC = status;
        },
        setInvalid(isInvalid) {
            this.invalid = isInvalid;
        },
        onCCModeChange() {
            if (this.ccMode == 'TEAM_MEMBER' && this.teamMemberOptions.length > 0) {
                this.selectedCC = this.teamMemberOptions[0].value;
            } else if (this.ccMode == 'OWNER' && this.ownerOptions.length > 0) {
                this.selectedCC = this.ownerOptions[0].value;
            } else if (this.ccMode == 'GUEST_LIST' && this.guestListOptions.length > 0) {
                this.selectedCC = this.guestListOptions[0].value;
            }
        },
        clearFile() {
            this.attachment = null;
        },
        clearMessage() {
            this.message = this.userEmailSignature || '';
        },
        setAmountToConfirm() {
            this.sumPaid = this.reservation.remainders.remain_to_deposit;
            this.$refs.form.setFieldValue('sumPaid', this.sumPaid);
        },
        setAmountToBalance() {
            this.sumPaid = this.reservation.remainders.remain_to_whole;
            this.$refs.form.setFieldValue('sumPaid', this.sumPaid);
        },
    },
    watch: {
        initialValues() {
            this.reservation = this.initialValues;
        },
    },
};
</script>

<style lang="scss" scoped>
:deep(.modal-dialog) {
    max-width: 650px !important;
}

.cc-email-input {
    margin-left: 5px;
}

.right-button {
    padding-top: 0.45rem !important;
    padding-bottom: 0.45rem !important;
    width: 100%;
    height: 2.75rem;
}
</style>
